import React, { useState, useMemo, useEffect } from 'react';
import { debounce } from 'lodash';
import { useDispatch } from 'react-redux';

import { CircularProgress } from '@material-ui/core';
import TablePagination from '@material-ui/core/TablePagination';

import Modal from '../../shared/modal/Modal';
import Input from '../../shared/Input';
import Select from '../../shared/Select';
import Table from '../../shared/table/TableRemote';
import ColorButton from '../../shared/ColorButton';
import AddButton from '../../shared/AddButton';

import { getPaginatedItems } from '../../../actions/itemsActions';

import { itemTypeOptions, setPositionOptions } from '../../../constants/enums';

import 'react-virtualized/styles.css';

const AddUsageItemsModal = (props) => {
  const {
    onSubmit,
    onClose,
    open,
    loading,
  } = props;

  const dispatch = useDispatch();

  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [fetching, setFetching] = useState(false);

  const [orderDirection, setOrderDirection] = useState('asc');
  const [orderBy, setOrderBy] = useState('code');
  const [filters, setFilters] = useState({ type: '' });
  const [search, setSearch] = useState('');

  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [numberOfDocs, setNumberOfDocs] = useState(0);
  const [, setTotalPages] = useState(1);

  const debouncedOnChange = useMemo(() => {
    return debounce((value) => setFilters({ ...filters, code: value }), 500);
  }, [setFilters]);

  useEffect(() => {
    fetchItems().catch();
  }, [orderBy, orderDirection, filters, open, currentPage, rowsPerPage]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    debouncedOnChange(e.target.value);
  };

  const fetchItems = async () => {
    try {
      if (!open) {
        return;
      }

      setFetching(true);

      const res = await dispatch(getPaginatedItems({
        page: currentPage,
        docsPerPage: rowsPerPage,
        orderBy: orderBy,
        orderDirection: orderDirection,
        filters,
      }));

      setNumberOfDocs(res?.count);
      setTotalPages(Math.ceil(res?.count / rowsPerPage));
      setItems(res?.items?.length ? res?.items : []);
    } catch (err) {
      console.error(err);
    } finally {
      setFetching(false);
    }
  };

  const handleCheck = (id) => {
    const item = items?.find((i) => i.id === id);
    let temp = selected.slice();

    if (temp?.map((i) => i.id)?.includes(item.id)) {
      temp = temp.filter((i) => i.id !== item.id);
    } else {
      temp = [...temp, { ...item, quantity: 1 }];
    }

    setSelected(temp);
  };

  const onMinusClick = (row) => {
    const temp = selected.slice();
    const item = temp?.find((i) => i.id === row.id);
    if (!item || item.quantity <= 1) {
      return;
    }

    item.quantity = item.quantity - 1;
    setSelected(temp);
  };

  const onPlusClick = (row) => {
    const temp = selected.slice();
    const item = temp?.find((i) => i.id === row.id);
    if (!item) {
      return;
    }

    item.quantity = item.quantity + 1;
    setSelected(temp);
  };

  const handleClose = () => {
    onClose();
    setSelected([]);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="xl"
    >
      <div className="add-usage-modal__container">
        <div className='add-usage-modal__title-container'>
          <div>
            <div className='font-bold font-size-bg'>Add Usage</div>
            <div className='m-t-sm font-size-sm'>From Item Master</div>
          </div>
          <div className='d-flex'>
            <Input
              type='search'
              placeholder='Search item code'
              value={search}
              onChange={handleSearch}
              backgroundColor='#fafafa'
            />
            <Select
              value={filters.type}
              onChange={(value) => setFilters({ ...filters, type: value })}
              options={[{ label: 'All', value: '' }, ...Object.values(itemTypeOptions)]}
              backgroundColor='#fafafa'
            />
          </div>
        </div>

        <div className='add-usage-modal__content'>
          <div className='add-usage-modal__main' style={{ padding: 0, margin: 0, flex: 1 }}>
            <div style={{ minWidth: 860, minHeight: 540, position: 'relative' }}>
              {fetching && (
                <div
                  className='d-flex p-lg flex-center align-center'
                  style={{
                    position: 'absolute',
                    height: '100%',
                    width: '100%',
                    background: 'rgba(227, 230, 233, 0.52)',
                    zIndex: 1000,
                    borderRadius: '8px'
                  }}
                >
                  <CircularProgress size={40} />
                </div>
              )}
              {items?.length ? (
                <ItemsTable
                  items={items}
                  handleCheck={handleCheck}
                  checkedItems={selected}
                  order={orderDirection}
                  setOrder={setOrderDirection}
                  orderBy={orderBy}
                  setOrderBy={setOrderBy}
                  page={currentPage}
                  setPage={setCurrentPage}
                  rowsPerPage={rowsPerPage}
                  setRowsPerPage={setRowsPerPage}
                  totalRows={numberOfDocs}
                  loading={fetching}
                  onPlusClick={onPlusClick}
                  onMinusClick={onMinusClick}
                />
              ) : (
                <div className="text-center secondary p-md">
                  No Items
                </div>
              )}
            </div>

            <div className='add-usage-modal__actions' style={{ justifyContent: 'space-between' }}>
              <TablePagination
                rowsPerPageOptions={[50, 100, 200]}
                count={numberOfDocs || items.length}
                page={currentPage}
                rowsPerPage={rowsPerPage}
                onPageChange={(e, value) => setCurrentPage(value)}
                onRowsPerPageChange={(e, value) => setRowsPerPage(value)}
                style={{ padding: '0 4px 4px 4px', flex: 1 }}
              />

              <div className='d-flex'>
                <div className="m-r-lg">
                  <ColorButton
                    text="Close"
                    color="blue"
                    onClick={onClose}
                    height={30}
                  />
                </div>

                <ColorButton
                  text='Add Items'
                  color='blue'
                  onClick={async () => {
                    await onSubmit(selected);
                    setSelected([]);
                  }}
                  loading={loading}
                  disabled={loading || !selected?.length}
                  height={30}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const ItemsTable = (props) => {
  const {
    items,
    handleCheck,
    checkedItems,
    handleCheckAll,
    order,
    setOrder,
    orderBy,
    setOrderBy,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalRows,
    loading,
    onPlusClick,
    onMinusClick,
  } = props;

  const columns = [
    { title: 'item code', field: 'code', type: 'default', sort: true },
    { title: 'description', field: 'description', type: 'default', sort: true },
    { title: 'type', field: 'typeName', type: 'default', sort: false },
    {
      title: '',
      field: 'quantity',
      type: 'custom',
      sort: false,
      formatter: (value, row) => {
        if (checkedItems?.find((i) => i.id === row.id)) {
          return (
            <div className="d-flex flex-end">
              <AddButton
                variant="remove"
                disabled={!checkedItems?.find((i) => i.id === row.id)?.quantity || checkedItems?.find((i) => i.id === row.id)?.quantity === 1}
                onClick={() => onMinusClick(row)}
                color={setPositionOptions.CONSIGNED.color}
              />
              <div
                style={{
                  color: setPositionOptions.CONSIGNED.color,
                  textAlign: 'center',
                  marginLeft: 4,
                  marginRight: 4,
                  width: 25,
                  fontWeight: 'bold'
                }}
              >
                {checkedItems?.find((i) => i.id === row.id)?.quantity || 1}
              </div>
              <AddButton
                variant="plus"
                onClick={() => onPlusClick(row)}
                color={setPositionOptions.CONSIGNED.color}
              />
            </div>
          );
        }
        return <div style={{ minWidth: 200 }} />
      }
    },
  ];


  return (
    <div style={{ width: '100%', height: '100%', overflow: 'auto' }}>
      <Table
        rows={items.map((item) => ({
          ...item,
          typeName: itemTypeOptions[item.type] ? itemTypeOptions[item.type].label : '',
        }))}
        columns={columns}
        handleCheck={handleCheck}
        checkedRows={checkedItems?.map((i) => i.id)}
        handleCheckAll={handleCheckAll}
        noCheckAll
        order={order}
        setOrder={setOrder}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        totalRows={totalRows}
        loading={loading}
        rowColor='#fafafa'
        rowBorderColor='#ffffff'
        variant='small'
      />
    </div>
  );
};

export default AddUsageItemsModal;
